import { splitRideSegments } from "../domain/ride-segments";
import { useMemo } from "react";
import { compileRideProgress, type RidePoint } from "../domain/ride";
import { measurementUnits, type UnitSystem } from "../domain/measurement-units";

interface ElevationProfileProperties {
  readonly points: readonly RidePoint[];
  readonly progress: number;
  readonly unitSystem?: UnitSystem;
}

/**
 * Draws a responsive elevation trace with current playback position.
 *
 * @param properties - Ride samples and playback progress.
 * @returns Accessible SVG elevation profile.
 */
export function ElevationProfile({ points, progress, unitSystem = "metric" }: ElevationProfileProperties) {
  const units = measurementUnits(unitSystem);
  const width = 720;
  const height = 150;
  const { minimum, maximum, line, area } = useMemo(() => {
    const minimum = Math.min(...points.map((point) => point.elevationMeters));
    const maximum = Math.max(...points.map((point) => point.elevationMeters));
    const range = Math.max(1, maximum - minimum);
    const distanceFractions = compileRideProgress(points).distanceFractions;
    const paths = splitRideSegments(points).map(segment => {
      const coordinates = segment.points.map((point, index) => {
        const x = distanceFractions[segment.startIndex + index]! * width;
        const y = height - ((point.elevationMeters - minimum) / range) * (height - 22) - 11;
        return `${index === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)}`;
      }).join(' ');
      const startX = distanceFractions[segment.startIndex]! * width;
      const endX = distanceFractions[segment.startIndex + segment.points.length - 1]! * width;
      return { line: coordinates, area: `${coordinates} L${endX},${height} L${startX},${height} Z` };
    });
    return { minimum, maximum, line: paths.map(path => path.line).join(' '), area: paths.map(path => path.area).join(' ') };
  }, [points]);
  const boundedProgress = Math.min(1, Math.max(0, progress));
  const markerX = boundedProgress * width;

  return (
    <svg
      aria-label={`Elevation profile from ${Math.round(units.elevation(minimum))} to ${Math.round(units.elevation(maximum))} ${units.elevationName}`}
      className="elevation-profile"
      role="img"
      preserveAspectRatio="none"
      viewBox={`0 0 ${width} ${height}`}
    >
      <defs>
        <linearGradient id="elevation-fill" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#f16929" stopOpacity="0.34" />
          <stop offset="100%" stopColor="#f16929" stopOpacity="0" />
        </linearGradient>
        <clipPath id="elevation-progress">
          <rect height={height} width={markerX} x="0" y="0" />
        </clipPath>
      </defs>
      <path className="elevation-area" d={area} />
      <path className="elevation-line elevation-line-muted" d={line} />
      <path className="elevation-line elevation-line-live" clipPath="url(#elevation-progress)" d={line} />
      <line className="elevation-marker" x1={markerX} x2={markerX} y1="0" y2={height} />
    </svg>
  );
}
